import { useEffect, useState } from 'react'
import { useLocalStorage } from '@rehooks/local-storage'
import { ConnectedUser } from '@ucc/admin-global-state'

import config from 'config'
import { Account } from 'types/Account'
import fetchPersonableAccounts from 'utils/fetchPersonableAccounts'

export interface ManagedAccountsByPartner {
  partnerAccount: Account
  customerAccounts: Account[]
}

const processData = (data: ManagedAccountsByPartner[]) =>
  data
    .filter(({ customerAccounts }) => customerAccounts.length)
    .map(({ customerAccounts, partnerAccount }) => ({
      customerAccounts: customerAccounts
        .map((account) => ({ ...account, name: account.name || account.key }))
        .sort(compareAccounts),
      partnerAccount: { ...partnerAccount, name: partnerAccount.name || partnerAccount.key },
    }))
    .sort(({ partnerAccount: account1 }, { partnerAccount: account2 }) => compareAccounts(account1, account2))

const compareAccounts = (account1: Account, account2: Account): number => account1.name.localeCompare(account2.name)

const useManagedAccounts = (): {
  loading: boolean
  managedAccounts: Account[]
  managedAccountsByPartners: ManagedAccountsByPartner[]
} => {
  const [iAmInfos] = useLocalStorage<ConnectedUser>(config.I_AM_TOKEN_KEY)
  const [loading, setLoading] = useState<boolean>(true)
  const [managedAccountsByPartners, setManagedAccountsByPartners] = useState<ManagedAccountsByPartner[]>([])
  const [managedAccounts, setManagedAccounts] = useState<Account[]>([])

  const accounts = iAmInfos?.accounts

  useEffect(() => {
    if (!accounts) {
      return
    }

    if (!accounts?.length || !iAmInfos?.key) {
      setManagedAccountsByPartners([])
      return
    }

    const partnerKeys = accounts.filter((account) => account.partner || account.parent).map((account) => account.key)

    if (!partnerKeys.length) {
      setLoading(false)
      return
    }
    if (partnerKeys) {
      fetchPersonableAccounts(partnerKeys)
        .then(processData)
        .then((sanitizedData) => {
          setManagedAccountsByPartners(sanitizedData)
          setManagedAccounts(sanitizedData.flatMap(({ customerAccounts }) => customerAccounts).sort(compareAccounts))
        })
        .finally(() => setLoading(false))
        .catch((err) => console.error(err))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(accounts), iAmInfos?.key])

  return {
    loading,
    managedAccounts,
    managedAccountsByPartners,
  }
}

export default useManagedAccounts
